import React from "react";

import { calculateMaxEstimatedCost } from "ComparePlans/calculateCost";
import PropTypes from "prop-types";
import formatCurrency from "Utils/formatCurrency";

import { BodyMedium } from "Cobalt/BodyText";
import Header from "Cobalt/Header";
import { Popover } from "Cobalt/Popover";
import BodyText from "Common/BodyText";
import PlanRecommendation from "Common/PlanRecommendation";
import SIOverlay from "Common/SIOverlay";
import { ContentRow } from "Common/Table";
import { tooltip } from "Common/TooltipOverlay/tooltipCopy";

import AveragePremiumRow from "./AveragePremiumRow";
import EligibilityBadge from "./Badge";
import HeaderWithTooltip from "./HeaderWithTooltip";
import SBCLinkCell from "./SBCLinkCell";
import TotalPremiumRow from "./TotalPremiumRow";

import type { MedicalPlanPrice } from "Utils/types";

type Props = {
  plans: Array<MedicalPlanPrice>;
  planButtons?: (arg0: MedicalPlanPrice) => React.ReactNode;
  isSampleQuote: boolean;
  isInRenewal?: boolean;
};

export default function Overview({ plans, planButtons, isSampleQuote, isInRenewal }: Props) {
  return (
    <tbody>
      <TotalPremiumRow plans={plans} />
      <AveragePremiumRow plans={plans} isSampleQuote={isSampleQuote} />
      {planButtons && <ContentRow cell={planButtons} items={plans} />}
      <ContentRow
        heading={
          <Header level={7} pt={0}>
            Recommended For
          </Header>
        }
        className=""
        cell={(plan) => <PlanRecommendation recommendation={plan.recommendation} />}
        items={plans}
      />

      <ContentRow
        heading={<HeaderWithTooltip level={3} text="Deductible" term="Deductible" />}
        cell={(p) => <BodyText size="medium">{formatCurrency(p.deductible)}</BodyText>}
        items={plans}
      />

      <ContentRow
        heading={<HeaderWithTooltip level={3} text="Maximum" term="Annual Out-of-Pocket Limit" />}
        cell={(p) => <BodyText size="medium">{formatCurrency(p.limit)}</BodyText>}
        items={plans}
      />

      <ContentRow
        heading={<BodyMedium>Plan Network</BodyMedium>}
        cell={(p) => (
          <SIOverlay
            placement="bottom"
            overlayContent={
              <Popover showBackdrop={false} id="compare-plans-network-type">
                {tooltip("Network", p.plan_type)}
              </Popover>
            }
            overlayTarget={
              <BodyText
                size="medium"
                style={{
                  borderBottom: "1px #a6a6a6 dashed",
                  display: "inline",
                  cursor: "pointer",
                }}
              >
                {p.plan_type}
              </BodyText>
            }
          />
        )}
        items={plans}
      />

      <ContentRow
        heading={<HeaderWithTooltip level={3} text="Doctor Visits" term="Estimated Doctor Visit" />}
        cell={(plan) => <BodyText size="medium">{formatCurrency(plan.estimated_costs.pcp_visit)}</BodyText>}
        items={plans}
      />

      <ContentRow
        heading={<HeaderWithTooltip level={3} text="Prescription Drugs" term="Estimated Prescription Drugs" />}
        cell={(plan) => (
          <BodyText size="medium">
            {formatCurrency(plan.estimated_costs.generic_drugs)}
            {" - "}
            {formatCurrency(plan.estimated_costs.brand_drugs)}
          </BodyText>
        )}
        items={plans}
      />

      <ContentRow
        heading={<HeaderWithTooltip level={3} text="Hospitalization" term="Estimated Hospitalization" />}
        cell={(plan) => <BodyText size="medium">{formatCurrency(calculateMaxEstimatedCost(plan, "hospitalization"))}</BodyText>}
        items={plans}
      />

      <ContentRow
        heading={<HeaderWithTooltip level={3} text="Plan Details" term="Summary of Benefits" />}
        cell={(plan) => <SBCLinkCell plan={plan} />}
        items={plans}
      />

      {isInRenewal && (
        <ContentRow
          heading={<HeaderWithTooltip level={3} text="Eligibility" term="Small Group Eligibility" />}
          cell={(plan) => <EligibilityBadge plan={plan} />}
          items={plans}
        />
      )}
    </tbody>
  );
}

Overview.propTypes = {
  plans: PropTypes.array.isRequired,
};
